import { ResourceRequestStatus, HiringStatus } from '~/types';
import { HIRING_STATUS_SUMMARY_COUNT_QUERY } from '~/modules/resourcing/components/HiringRequestSummary/hooks/useHiringStatusSummaryCount';

export const tryLoadHiringStatusSummaryCountCachedQuery = ({
  proxy,
  optimistic,
  hiringStatusSummaryCountQueryVariables
}) => {
  try {
    return proxy.readQuery(
      {
        query: HIRING_STATUS_SUMMARY_COUNT_QUERY,
        variables: hiringStatusSummaryCountQueryVariables
      },
      optimistic
    );
  } catch (e) {
    return null;
  }
};

export const calculateNewHiringStatusCountForNonToBeHiredRequests = ({
  summaryCount,
  oldHiringStatus
}) => {
  const newSummaryCount = { ...summaryCount };

  if (oldHiringStatus === HiringStatus.HiringWithdrawn) {
    newSummaryCount.hiringWithdrawnCount += 1;
  } else {
    newSummaryCount.noHiringStatusCount += 1;
  }

  return newSummaryCount;
};

export const calculateNewHiringStatusCountForToBeHiredRequest = ({
  summaryCount,
  oldHiringStatus,
  newHiringStatus,
  newRequestStatus
}) => {
  const newSummaryCount = { ...summaryCount };

  switch (oldHiringStatus) {
    case HiringStatus.HiringRequested:
      newSummaryCount.hiringRequestedCount -= 1;

      if (
        newHiringStatus === HiringStatus.HiringWithdrawn &&
        newRequestStatus === ResourceRequestStatus.Tobehired
      ) {
        newSummaryCount.hiringWithdrawnCount += 1;
      }

      return newSummaryCount;
    case HiringStatus.HiringWithdrawn:
      newSummaryCount.hiringWithdrawnCount -= 1;

      if (newHiringStatus === HiringStatus.HiringRequested) {
        newSummaryCount.hiringRequestedCount += 1;
      }

      return newSummaryCount;
    default:
      newSummaryCount.noHiringStatusCount -= 1;
  }

  if (newHiringStatus === HiringStatus.HiringRequested) {
    newSummaryCount.hiringRequestedCount += 1;
  }

  return newSummaryCount;
};

export const calculateNewHiringStatusSummaryCount = ({
  oldHiringStatus,
  newHiringStatus,
  newRequestStatus,
  cachedSummaryCount,
  existingRequestStatus
}) => {
  const {
    hiringStatusSummaryCount: {
      noHiringStatusCount,
      hiringRequestedCount,
      hiringWithdrawnCount
    }
  } = cachedSummaryCount;

  const newSummaryCount = {
    noHiringStatusCount,
    hiringRequestedCount,
    hiringWithdrawnCount
  };

  if (
    existingRequestStatus === ResourceRequestStatus.Submitted ||
    existingRequestStatus === ResourceRequestStatus.Rejected ||
    existingRequestStatus === ResourceRequestStatus.Draft
  ) {
    return calculateNewHiringStatusCountForNonToBeHiredRequests({
      summaryCount: newSummaryCount,
      oldHiringStatus
    });
  }

  return calculateNewHiringStatusCountForToBeHiredRequest({
    summaryCount: newSummaryCount,
    oldHiringStatus,
    newHiringStatus,
    newRequestStatus
  });
};

export const updateHiringStatusSummaryCountCache = ({
  responseType,
  newRequestStatus,
  oldHiringStatus,
  existingRequestStatus,
  hiringStatusSummaryCountQueryVariables
}) => (proxy, mutationResponse) => {
  if (
    !(
      existingRequestStatus === ResourceRequestStatus.Tobehired ||
      existingRequestStatus === ResourceRequestStatus.Submitted ||
      existingRequestStatus === ResourceRequestStatus.Rejected ||
      existingRequestStatus === ResourceRequestStatus.Draft
    )
  )
    return;

  const {
    data: {
      [responseType]: { isOptimistic, resourceRequest: newRequest }
    }
  } = mutationResponse;

  const cachedSummaryCount = tryLoadHiringStatusSummaryCountCachedQuery({
    proxy,
    optimistic: isOptimistic,
    hiringStatusSummaryCountQueryVariables
  });

  if (!cachedSummaryCount) return;

  const newSummaryCount = calculateNewHiringStatusSummaryCount({
    oldHiringStatus,
    newHiringStatus: newRequest?.sourceMetadata?.hiringStatus,
    newRequestStatus,
    cachedSummaryCount,
    existingRequestStatus
  });

  proxy.writeQuery({
    query: HIRING_STATUS_SUMMARY_COUNT_QUERY,
    variables: hiringStatusSummaryCountQueryVariables,
    data: {
      hiringStatusSummaryCount: {
        ...cachedSummaryCount.hiringStatusSummaryCount,
        ...newSummaryCount
      }
    }
  });
};
